<script setup>
/**
 * An encrypted org, in Settings' Sync tab (stores/sync.js): how its sync
 * stands, the devices waiting for the key (six digits to compare, or their QR
 * code scanned — the fingerprint in it doing the comparing), and what was
 * merged. The org itself (its picker, its people, adding a device) is
 * CloudPanel's, and where the ledger lives is LedgerStates'. `approve` is a
 * waiting device's code opened here by a camera app (main.js).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Icon from '../ui/Icon.vue'
import { state, toast } from '../../stores/state'
import { errorText, t } from '../../stores/i18n'
import * as sync from '../../stores/sync'
import { canScan, startScanner } from '../../camera'

const props = defineProps({ approve: { type: Object, default: null } })

const busy = ref('')
const error = ref('')
const conflicts = ref(null)

async function attempt(what, fn) {
  busy.value = what
  error.value = ''
  try {
    return await fn()
  } catch (err) {
    error.value = err?.name === 'NotAllowedError' ? t('cloud.passkeyCancelled') : errorText(err)
  } finally {
    busy.value = ''
  }
}

onMounted(() => {
  sync.refreshWaiting()
  if (props.approve) checkScanned(props.approve)
})

// ------------------------------------------------------------------ scanning
const scanner = canScan()
const video = ref(null)
const scanning = ref(false)
let stopCamera = null
async function scan() {
  error.value = ''
  scanning.value = true
  try {
    stopCamera = await startScanner(video.value, onCode)
  } catch (err) {
    scanning.value = false
    error.value = t('device.noCamera', { reason: err?.message ?? String(err) })
  }
}
function stopScan() {
  stopCamera?.()
  stopCamera = null
  scanning.value = false
}
function onCode(text) {
  const code = sync.readSyncCode(text)
  if (!code) return (error.value = t('device.notOurs'))
  stopScan()
  // An org's device link: boot signs in and opens that org.
  if (code.kind === 'join') location.assign(code.url)
  else if (!state.sync.on || state.sync.status === 'locked') error.value = t('sync.approveElsewhere')
  else checkScanned(code)
}
onBeforeUnmount(stopScan)

/** A waiting device's code, checked against the key the server has for it. */
const scanned = ref(null)
const checkScanned = (code) =>
  attempt('scanned', async () => {
    scanned.value = await sync.scannedDevice(code)
  })
const approveScanned = () =>
  attempt('scanned', async () => {
    await sync.approve(scanned.value.id)
    toast(t('sync.approved', { name: scanned.value.name || scanned.value.email }), 'good')
    scanned.value = null
  })

// ------------------------------------------------------------------ an encrypted org
const approve = (d) =>
  attempt(`approve:${d.id}`, async () => {
    await sync.approve(d.id)
    toast(t('sync.approved', { name: d.name || d.email }), 'good')
  })
const showConflicts = () =>
  attempt('conflicts', async () => {
    conflicts.value = conflicts.value ? null : await sync.conflicts(50)
  })
watch(
  () => state.sync.status,
  (s) => s === 'synced' && sync.refreshWaiting(),
)

const STATUS_ICON = { synced: 'check', syncing: 'repeat', connecting: 'repeat', offline: 'alert', locked: 'lock', 'schema-ahead': 'alert', error: 'alert', gone: 'alert', 'signed-out': 'alert' }
const statusTone = computed(() => (state.sync.status === 'synced' ? 'text-accent' : ['syncing', 'connecting'].includes(state.sync.status) ? 'text-muted' : 'text-warn'))
</script>

<template>
  <div class="grid gap-5" :data-sync="state.sync.on ? 'on' : 'off'">
    <section :class="scanning ? 'grid gap-2' : 'hidden'" data-sync-scanner>
      <video ref="video" class="aspect-square w-full rounded-xl bg-black object-cover" muted playsinline />
      <button class="btn-ghost justify-self-start" @click="stopScan">{{ t('common.cancel') }}</button>
    </section>
    <section v-if="scanned" class="grid gap-2 rounded-xl border border-accent/40 p-3" data-sync-scanned>
      <p class="flex items-center gap-2 text-[13.5px]">
        <Icon name="check" :size="15" class="text-accent" />{{ t('sync.scannedVerified', { name: scanned.name || t('sync.aDevice') }) }}
      </p>
      <p class="text-[12px] text-faint">{{ scanned.email }}</p>
      <div class="flex gap-2">
        <button class="btn-primary" :disabled="busy === 'scanned'" @click="approveScanned">{{ t('sync.approve') }}</button>
        <button class="btn-ghost" @click="scanned = null">{{ t('common.cancel') }}</button>
      </div>
    </section>

    <!-- An encrypted org. -->
    <template v-if="state.sync.on">
      <section class="grid gap-1" data-sync-status :data-status="state.sync.status">
        <p class="flex items-center gap-2 text-[14px]">
          <Icon :name="STATUS_ICON[state.sync.status] ?? 'repeat'" :size="16" :class="statusTone" />
          <span>{{ t(`sync.status.${state.sync.status}`) }}</span>
        </p>
        <p v-if="state.sync.error" class="text-[12.5px] text-warn">{{ state.sync.error }}</p>
        <p v-if="state.sync.editing" class="text-[12.5px] text-muted">{{ t('sync.editing', { user: state.sync.editing.user ?? '' }) }}</p>
        <p v-if="state.sync.outbox" class="text-[12.5px] text-muted" data-outbox>{{ t('sync.outbox', { count: state.sync.outbox }) }}</p>
        <p v-if="state.sync.outbox && state.sync.outboxError" class="text-[12.5px] text-warn" data-outbox-error>
          {{ t('sync.outboxFailed', { reason: errorText({ code: state.sync.outboxError, message: state.sync.outboxError }) }) }}
        </p>
      </section>

      <section v-if="state.sync.waiting.length" class="grid gap-2" data-sync-waiting>
        <p class="text-[12.5px] font-semibold text-muted">{{ t('sync.waitingTitle') }}</p>
        <p class="text-[12px] text-faint">{{ t('sync.waitingLead') }}</p>
        <div v-for="d in state.sync.waiting" :key="d.id" class="flex items-center gap-3 rounded-xl border border-line-soft p-3" :data-waiting="d.id">
          <div class="min-w-0 flex-1">
            <p class="truncate text-[13.5px]">{{ d.name || t('sync.aDevice') }}</p>
            <p class="truncate text-[12px] text-faint">{{ d.email }}</p>
          </div>
          <span class="font-mono text-[15px] tracking-wider" data-waiting-code>{{ d.code }}</span>
          <button class="btn-soft" :disabled="busy === `approve:${d.id}`" @click="approve(d)">{{ t('sync.approve') }}</button>
        </div>
      </section>

      <div class="flex flex-wrap gap-2">
        <button class="btn-soft" @click="sync.syncNow()"><Icon name="repeat" :size="15" /> {{ t('sync.now') }}</button>
        <button v-if="scanner && !scanning" class="btn-soft" @click="scan"><Icon name="scan" :size="15" /> {{ t('sync.scanDevice') }}</button>
        <button v-if="state.sync.conflicts" class="btn-ghost" data-sync-conflicts @click="showConflicts">
          {{ t('sync.conflicts', { count: state.sync.conflicts }) }}
        </button>
      </div>
      <ul v-if="conflicts" class="grid gap-1 text-[12px] text-muted">
        <li v-for="c in conflicts" :key="c.id" class="truncate">
          {{ c.at.slice(0, 16).replace('T', ' ') }} · {{ c.tbl }}<template v-if="c.col">.{{ c.col }}</template> ·
          {{ t(`sync.why.${c.why.startsWith('constraint') ? 'constraint' : c.why}`) }}
        </li>
      </ul>
    </template>

    <p v-if="error" class="text-[12.5px] text-warn" role="alert">{{ error }}</p>
  </div>
</template>
